import { AfterViewInit, Component, Input, NgZone, OnChanges, OnDestroy, SimpleChanges, inject } from '@angular/core';
import * as Highcharts from 'highcharts';
import {EmptyChartComponent} from "../../empty-state/empty-chart/empty-chart.component";
import {
  CHART_FONTS,
  CHART_STYLES,
  HighchartsOptions
} from "../../shared/component/highcharts-common/highcharts-global-const";


/**
 * This is BASE BAR CHART
 *
 * ▒█▀▀█ ▒█░▒█ ░█▀▀█ ▒█▄░▒█ ▒█▀▀█ ▒█▀▀▀ ▒█▀▀▀█ 　 ▒█▄░▒█ ▒█▀▀▀█ ▀▀█▀▀ 　 ░█▀▀█ ▒█░░░ ▒█░░░ ▒█▀▀▀█ ▒█░░▒█ ▒█▀▀▀ ▒█▀▀▄
 * ▒█░░░ ▒█▀▀█ ▒█▄▄█ ▒█▒█▒█ ▒█░▄▄ ▒█▀▀▀ ░▀▀▀▄▄ 　 ▒█▒█▒█ ▒█░░▒█ ░▒█░░ 　 ▒█▄▄█ ▒█░░░ ▒█░░░ ▒█░░▒█ ▒█▒█▒█ ▒█▀▀▀ ▒█░▒█
 * ▒█▄▄█ ▒█░▒█ ▒█░▒█ ▒█░░▀█ ▒█▄▄█ ▒█▄▄▄ ▒█▄▄▄█ 　 ▒█░░▀█ ▒█▄▄▄█ ░▒█░░ 　 ▒█░▒█ ▒█▄▄█ ▒█▄▄█ ▒█▄▄▄█ ▒█▄▀▄█ ▒█▄▄▄ ▒█▄▄▀
 * to make changes here, ask Ivan or Dmytro
 *
 * How to use:
 *  Very simple, just add this in your component
 *
 *  <app-base-bar-chart
 *    *ngIf="chart.showCharts"                            // Is needed only when you want to set another data
 *    [chartId]="'lol-kek-cheburek'"                      // Chart id, needed for html rendering
 *    [additionalOptions]="data.data.veryImportant.data"> // Object, which contains additional options: HighchartsOptions.
 *   </app-base-bar-chart>
 *
 * How to fill additional options:
 *     Create separate .ts service for chart options calculations
 *
 *     EXAMPLE -> also check real example FairValueAndPpChartsOptionsService
 *
 *     private static createChartOptions(chartData: PortfolioPerformance): FairValueChartInformation {
 *          const preparedSimpleArray1 = collectUsingMagicThisData(chartData);
 *          const preparedSimpleArray2 = collectUsingMagicThisData(chartData);
 *          const preparedSimple_X_AXIS_Array = collectUsingMagicThisData(chartData);
 *
 *          return {
 *            chartId,
 *            additionalOptions: {
 *
 *              tooltip: CHART_STYLES.yourTooltipIfNeeded,
 *              series: [{
 *                name: 'Nice name1 for nice data',
 *                data: preparedSimpleArray1,
 *              },
 *              {
 *                name: 'Nice name2 for nice data',
 *                data: preparedSimpleArray2,
 *              }
 *              ],
 * IMPORTANT: to add the WHOLE xAxis, to add only categories is not enough, in order to save styles
 *              xAxis: [{
 *                   categories: preparedSimple_X_AXIS_Array,
 *                   type: 'category',
 *                   crosshair: true,
 *                   style: { color: '#333333', fontSize: CHART_FONTS.font4 },
 *                   labels: {
 *                     rotation: -45, // Rotate labels
 *                     style: {
 *                       width: 75,
 *                       textOverflow: 'ellipsis',
 *                       fontSize: CHART_FONTS.font4,
 *                     },
 *                   },
 *                   scrollbar: CHART_STYLES.scrollbar,
 *                 }],
 *            },
 *          };
 *        }
 *
 */
@Component({
  selector: 'app-base-bar-chart',
  templateUrl: './base-bar-chart.component.html',
  styleUrls: ['./base-bar-chart.component.scss'],
  imports: [
    EmptyChartComponent,
  ]
})
export class BaseBarChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  private zone = inject(NgZone);

  @Input() additionalOptions: Partial<HighchartsOptions> = {};
  @Input() chartId: string;
  @Input() height = 'h-[28rem]'; // default height

  options: HighchartsOptions;
  chartInstance: Highcharts.Chart;
  showEmptyState = true;

  ngAfterViewInit(): void {
    this.initializeOptions();
    // this.renderChart();  // was removed => prevent double loading
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.additionalOptions) {
      this.initializeOptions();
      setTimeout(() => {
        if (document.getElementById(this.chartId)) {
          this.reRenderChart();
        }
      }, 250);
    }
    /**
     * New height needed for printable version.
     * We need to save all other option and use redraw() for it.
     * If use reRenderChart() in this case, chart will be rerendered with initial options
    */
    if (changes.height && this.chartInstance) {
      this.chartInstance.redraw();
    }
  }

  ngOnDestroy(): void {
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
  }

  initializeOptions(): void {
    const defaultOptions: HighchartsOptions = {
      chart: {
        type: 'column',
        zoomType: 'xy',
        reflow: true,
        style: {
          fontFamily: 'Inter',
        },
        events: CHART_STYLES.eventForScrollingWithManualRedraw,
        zooming: {
          mouseWheel: false,
        },
      },
      customRange: 14, // number of x-axis values in the scroll range
      title: {
        text: undefined,
        style: CHART_STYLES.title,
        align: 'left',
      },
      xAxis: {
        type: 'category',
        crosshair: true,
        style: { color: '#333333', fontSize: CHART_FONTS.font4 },
        labels: {
          rotation: -45,
          style: {
            width: 100,
            textOverflow: 'ellipsis',
            fontSize: CHART_FONTS.font4,
          },
        },
        scrollbar: CHART_STYLES.scrollbar,
      },
      yAxis: {
        title: {
          text: undefined,
        },
        labels: CHART_STYLES.yAxisLabelsStyleWithM,
        stackLabels: CHART_STYLES.grandTotalStackLabels(),
      },
      exporting: CHART_STYLES.hiddenExportingButton,
      tooltip: CHART_STYLES.customFairValueTooltipWithLine,
      legend: CHART_STYLES.legend(),
      plotOptions: {
        column: {
          stacking: 'normal',
        },
      },
      credits: {
        enabled: false,
      },
      series: [],
      lang: CHART_STYLES.lang,
    };

    this.options = { ...defaultOptions, ...this.additionalOptions };

    this.checkEmptyState();
  }

  private reRenderChart(): void {
    this.zone.runOutsideAngular(() => {
      if (document.getElementById(this.chartId)) {
        if (this.chartInstance) {
          this.chartInstance.destroy();
        }
        this.chartInstance = Highcharts.chart(this.chartId, this.options);
      }
    });
  }

  private checkEmptyState(): void {
    this.showEmptyState = this.options.series.every((series) => {
      // Check if data is undefined or empty
      if (!series.data || series.data.length === 0) {
        return true;
      }

      // Check if all values are 0
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return
      return series.data.every((point: any) => point === 0);
    });
  }
}
